import type { PixelEvent } from '@/events';

import type { BatchConfig, BatchDebugState, IBatchManager } from './types';

export class BatchManager implements IBatchManager {
  private buffer: PixelEvent[] = [];

  private oldestAt: number | null = null;

  add(events: readonly PixelEvent[]): void {
    if (events.length === 0) return;

    if (this.buffer.length === 0) {
      this.oldestAt = Date.now();
    }

    this.buffer.push(...events);
  }

  takeBatch(maxSize?: number): PixelEvent[] {
    if (this.buffer.length === 0) return [];

    const limit = maxSize ?? this.buffer.length;
    const batch = this.buffer.splice(0, limit);

    if (this.buffer.length === 0) {
      this.oldestAt = null;
    }

    return batch;
  }

  peek(): readonly PixelEvent[] {
    return [...this.buffer];
  }

  size(): number {
    return this.buffer.length;
  }

  clear(): void {
    this.buffer = [];
    this.oldestAt = null;
  }

  shouldFlush(config: BatchConfig): boolean {
    if (this.buffer.length === 0) return false;
    if (!config.enabled) return true;
    if (this.buffer.length >= config.batchSize) return true;

    if (
      this.oldestAt !== null &&
      Date.now() - this.oldestAt >= config.maxWaitMs
    ) {
      return true;
    }

    return false;
  }

  getState(): BatchDebugState {
    return {
      bufferSize: this.buffer.length,
      oldestEventAt: this.oldestAt,
      pendingBatches: this.buffer.length,
    };
  }
}
